// opervex-frontend/src/pages/MaterialManagement/GoodsMovement.js

import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Form, InputGroup, Card, Button, Table, Modal } from "react-bootstrap";
import { FaSearch, FaPlus, FaSave, FaPencilAlt, FaTrash, FaRegListAlt, FaPrint } from "react-icons/fa";
import { RiErrorWarningLine } from "react-icons/ri";
import { MdOutlineCancel } from "react-icons/md";
import * as XLSX from "xlsx";

import MaskedFormControl from "react-bootstrap-maskedinput";

import { useAuth } from "../../contexts/AuthContext";
import { SearchHelpModal } from "../../utils/SearchHelp";
import { SelectModal } from "../../utils/SelectModal";
import OpervexStorage from "../../utils/OpervexStorage";

import useOpervex from "../../hooks/useOpervex";
import QuantityCustom from "../../components/CustomInput/customQuantity";
import CustomCurrency from "../../components/CustomInput/customCurrency";
import ConfirmService from "../../components/ConfirmationModal/ConfirmService";
import ReasonPromptService from "../../services/ReasonPromptService";
import { useLoading } from "../../contexts/LoadingContext";
import FileSelectorService from "../../services/FileSelectorService";

const rolePageRequire = "GOODS_MOVEMENT";

var dataDefault = {
  header: {
    id: "",
    description: "",
    operation: null,
    documentDate: null,
    amount: 0,
    originDeposit: null,
    destinationDeposit: null,
    reference: "",
    accesskey: "",
    reason: "",
    reversalType: null,
    reversal: null,
    orderService: null,
  },

  items: [],

  texts: {
    destinationDeposit: "",
    originDeposit: "",
  },

  selectRow: {},
};

const actionsDefault = {
  create: true,
  change: true,
  delete: true,
  save: true,
  reversal: true,
  print: true,
};

const OperationGoodsMovement = () => {
  const originalValues = useRef({});
  const { roles, loading } = useAuth();
  const [showSerials, setShowSerials] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [disabledField, setDisabledField] = useState(true);
  const [actions, setActions] = useState(actionsDefault);
  const [data, setData] = useState(dataDefault);
  const [priceDifference, setPriceDifference] = useState(0);
  const [operation, setOperation] = useState({});
  const [errors, setErrors] = useState(null);
  const { id } = useParams();
  const OpervexAPI = useOpervex();
  const { setLoadingScreen } = useLoading();

  const fetchData = async () => {
    await commandAction({ ...actionsDefault, create: false });

    if (id) await searchAPI(id);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const commandAction = async (actions, id = null) => {
    const command = `${rolePageRequire}_CODE_${id}`;
    const hasPermission = roles.find((role) => role.command === command);

    if (!actions.create)
      actions.create = !roles.find(
        (role) => role.command.startsWith(rolePageRequire) && role.actions.some((action) => action.key === "Create"),
      );

    if (!hasPermission) {
      setActions({ ...actionsDefault, create: actions.create });
      return;
    }

    if (!actions.reversal)
      actions.reversal = !roles.find(
        (role) => role.command === command && role.actions.some((action) => action.key === "Reversal"),
      );

    if (!actions.print)
      actions.print = !roles.find((role) => role.command === command && role.actions.some((action) => action.key === "Print"));

    actions.change = true;

    setActions({ ...actions });
  };

  const handleFocus = (event, index, name, row) => {
    originalValues.current[`${index}_${name}`] = event.target.value;
  };

  const handleBlur = (event, index, name, row) => {
    const originalValue = originalValues.current[`${index}_${name}`];
    const currentValue = event.target.value;

    if (originalValue === currentValue) return;

    switch (name) {
      case "product":
        // Aqui chama o handleSelectProduct com index e id normalmente
        handleSelectProduct({ index: index, id: row ? row.product : null, onClick: false });
        break;
      case "originDeposit":
        handleStorageLocation(true, false, currentValue);
        break;
      case "destinationDeposit":
        handleStorageLocation(false, false, currentValue);
        break;
      default:
        break;
    }
  };

  const handleSelectProduct = async (obj) => {
    let product = null;
    let item = null;

    if (obj.onClick) {
      if (operation.visibleDepOrigin && data.header.originDeposit) {
        item = await SearchHelpModal({
          urlData: "/MaterialManagement/Stock/SearchHelp",
          schema: await OpervexAPI.MaterialManagement.Stock.schema(),
          preFilters: [
            { fieldname: "depositId", value: data.header.originDeposit },
            { fieldname: "disabled", value: false },
          ],
        });
      } else {
        item = await SearchHelpModal({
          urlData: "/MaterialManagement/Product/SearchHelp",
          schema: await OpervexAPI.MaterialManagement.Products.schema(),
          preFilters: [{ fieldname: "disabled", value: false }],
        });
      }
    } else item = obj;

    if (item && item.id) {
      product = await OpervexAPI.MaterialManagement.Products.findId(item.id);
    }

    if (product) {
      setData((prev) => ({
        ...prev,
        items: prev.items.map((row, i) =>
          i === obj.index
            ? {
                ...row,
                product: product.id,
                description: product.description,
                unitMeasurement: product.unitMeasurement,
                controlSerial: product.controlSerial,
                controlMac: product.controlMac,
              }
            : row,
        ),
      }));
    }
  };

  const calculateDifference = (currentData = data, currentOperation = operation) => {
    if (!currentOperation.visibleAmount) {
      setPriceDifference(0);
      return;
    }

    const totalItems = Number(
      currentData.items.reduce((total, item) => total + Number(item.quantity ?? 0) * Number(item.priceUnit ?? 0), 0).toFixed(2),
    );

    setPriceDifference(Number(currentData.header.amount || 0) - totalItems);
  };

  useEffect(() => {
    calculateDifference(data, operation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.header.amount, data.items, operation.visibleAmount]);

  if (loading) {
    return <div>Carregando...</div>;
  }

  const handleSerials = async (index, row) => {
    setData((prev) => {
      const newSelectRow = { ...row, index };

      // Garante que `serials` existe (inicializa como array vazio se não existir)
      newSelectRow.serials = newSelectRow.serials || [];

      // Verifica se precisa ajustar os seriais
      if (newSelectRow.quantity !== newSelectRow.serials.length) {
        if (newSelectRow.quantity > newSelectRow.serials.length) {
          // Adiciona seriais vazios (ou padrão) até atingir a quantidade
          const missingItems = newSelectRow.quantity - newSelectRow.serials.length;
          const newSerials = [...newSelectRow.serials];
          for (let i = 0; i < missingItems; i++) {
            newSerials.push({
              serialNumber: null,
              mac: null,
            });
          }
          newSelectRow.serials = newSerials;
        } else {
          // Remove seriais excedentes
          newSelectRow.serials = newSelectRow.serials.slice(0, newSelectRow.quantity);
        }

        newSelectRow.serials.map((item, index) => {
          return (item.sequential = index + 1);
        });
      }

      return { ...prev, selectRow: newSelectRow };
    });

    setShowSerials(true);
  };

  const handleSelectSerial = async () => {
    if (!data.selectRow.product || !data.header.originDeposit) return;

    const results = await OpervexAPI.MaterialManagement.StockSerial.findByProductDeposit(
      data.selectRow.product,
      data.header.originDeposit,
    );

    let items = [];

    results?.forEach((result) => {
      const selected = data.selectRow.serials.find((x) => x.serialNumber === result.serialNumber && x.mac === result.mac);

      items.push({
        serialNumber: result.serialNumber,
        mac: result.mac,
        selected,
      });
    });

    let selecteds = await SelectModal({
      title: "Seleção de Nº Séries/MAC",
      multiple: true,
      maxSelection: data.selectRow.quantity,
      fields: [
        { key: "serialNumber", description: "Nº Serial" },
        { key: "mac", description: "MAC" },
      ],
      items: items,
    });

    if (!selecteds) return;

    selecteds.map((item, index) => {
      delete item.selected;

      return (item.sequential = index + 1);
    });

    setData((prev) => ({
      ...prev,
      selectRow: {
        ...prev.selectRow,
        serials: selecteds,
      },
    }));
  };

  const handleSelectOperation = async (item) => {
    if (item && item.id) {
      const operation = await OpervexAPI.System.OperationGoodsMovement.findId(item.id);

      setPriceDifference(0);

      let header = { ...dataDefault.header, operation: item.id };
      let items = [];
      let idProducts = [];
      let products = [];

      if (operation.visibleAcessKey) {
        const result = await ConfirmService.show("Importação", "Deseja importar um XML para pré preencher o documento?");

        if (result) {
          const file = await FileSelectorService.selectFile();

          const nfe = await OpervexAPI.Tax.Inbound.NFe.convertXML(file);

          if (nfe) {
            header.accesskey = nfe.nfeProc.protNFe.infProt.chNFe;
            header.documentDate = new Date(nfe.nfeProc.NFe.infNFe.ide.dhEmi).toISOString().split("T")[0];
            header.reference =
              String(nfe.nfeProc.NFe.infNFe.ide.nNF).padStart(9, "0") +
              "-" +
              String(nfe.nfeProc.NFe.infNFe.ide.serie).padStart(3, "0");
            header.amount = nfe.nfeProc.NFe.infNFe.total.ICMSTot.vNF;

            header.description = nfe.nfeProc.NFe.infNFe.infAdic.infCpl.substring(0, 50);

            nfe.nfeProc.NFe.infNFe.det.forEach((det) => {
              idProducts.push("'" + det.prod.cProd + "'");
            });

            if (idProducts.length > 0)
              products = (await OpervexAPI.MaterialManagement.Products.findAllByExternalCode(idProducts)) ?? [];

            nfe.nfeProc.NFe.infNFe.det.forEach((det) => {
              let item = {};

              item.item = det.$.nItem;
              item.quantity = det.prod.qCom;
              item.unitMeasurement = det.prod.uCom;
              item.priceUnit = det.prod.vUnCom;
              item.serials = [];

              const product = products.find((x) => x.externalCode === det.prod.cProd);

              item.product = product?.id;
              item.description = product?.description;
              item.controlSerial = product?.controlSerial;
              item.controlMac = product?.controlMac;

              items.push(item);
            });
          }
        }

        if (operation.visibleKardex) {
          const kardesData = await getKardexData();

          if (items && items.length) {
            items.forEach((item) => {
              if (item.controlSerial) {
                const product = products.find((x) => x.id === item.product);
                const serials = kardesData.serials.filter((x) => x.product === product.externalCode);
                serials.forEach((x) => {
                  if (item.serials.length <= item.quantity)
                    item.serials.push({
                      sequential: item.serials.length + 1,
                      serialNumber: x.serial,
                      mac: x.mac,
                    });
                });
              }
            });
          }
        }
      }

      setData({ ...dataDefault, header: header, items: items });
      commandAction({ ...actionsDefault, save: false }, operation.id);
      setOperation(operation);
      setDisabledField(false);
    }
  };

  const getKardexData = async () => {
    const resultKardex = await ConfirmService.show("Importação", "Deseja importar arquivo Kardex para pré-validação do documento?");

    if (!resultKardex) return;

    const fileKardex = await FileSelectorService.selectFile();

    if (!fileKardex) return;

    const buffer = await fileKardex.arrayBuffer();

    const workbook = XLSX.read(buffer, { type: "array" });

    const products = XLSX.utils
      .sheet_to_json(workbook.Sheets["1-Produtos"], {
        range: 1,
      })
      .map((item) => ({
        product: item["Cod Produto"],
        deposit: item.Armaz,
        description: item.Descricao,
        unit: item.Unid,
        quantity: Number(String(item.Quant).replace(".", "").replace(",", ".")),
      }));

    const serials = XLSX.utils
      .sheet_to_json(workbook.Sheets["2-Seriais"], {
        range: 1,
      })
      .map((item) => ({
        product: item["Cod Produto"],
        serial: item.Serial,
        mac: item.Mac,
        quantity: Number(String(item.Quant).replace(".", "").replace(",", ".")),
      }));

    return {
      products,
      serials,
    };
  };

  const handleAddRow = () => {
    setData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          item: data.items.length + 1,
          product: "",
          description: "",
          quantity: 0,
          unitMeasurement: "",
          priceUnit: 0,
          controlSerial: false,
          controlMac: false,
          serials: [],
        },
      ],
    }));
  };

  const handleRemoveRow = (index) => {
    setData((prev) => {
      // Remove o item com base no índice
      const updatedItems = prev.items.filter((_, i) => i !== index);

      // Reatribui a sequência com base na nova ordem
      const reindexedItems = updatedItems.map((product, i) => ({
        ...product,
        item: i + 1,
      }));

      return {
        ...prev,
        items: reindexedItems,
      };
    });
  };

  const handleChangeRow = async (index, e) => {
    const { name, value } = e.target;

    let _value = value;

    if (name === "unitMeasurement") _value = _value.toUpperCase();

    setData((prev) => ({
      ...prev,
      items: prev.items.map((row, i) => (i === index ? { ...row, [name]: _value } : row)),
    }));
  };

  const handleChangeRowSerial = async (index, e) => {
    const { name, value } = e.target;

    let _value = value;
    setData((prev) => ({
      ...prev, // mantém todo o estado anterior
      selectRow: {
        ...prev.selectRow, // mantém todas as outras propriedades de selectRow
        serials: prev.selectRow.serials.map((row, i) =>
          i === index
            ? { ...row, [name]: _value } // atualiza apenas o campo com nome [name] no índice específico
            : row,
        ),
      },
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    var newData = { ...data };

    const group = name.split(".")[0];
    const field = name.split(".")[1];

    switch (group) {
      case "header":
        newData = { ...data, header: { ...data.header, [field]: type === "checkbox" ? checked : value } };
        break;

      default:
        break;
    }

    setData(newData);
  };

  const handleStorageLocation = async (origin, onClick, value) => {
    if (disabledField) return;

    let preFilters = [{ fieldname: "disabled", value: false }];
    let urlData = null;
    let id = null;

    if (onClick) {
      if (origin) {
        preFilters.push({ fieldname: "type", value: operation.typeDepOrigin });
        urlData = "/MaterialManagement/StorageLocation/SearchHelp";
      }

      if (!origin) {
        preFilters.push({ fieldname: "type", value: operation.typeDepDestination });
        urlData = "/MaterialManagement/StorageLocation/SearchHelp";
      }

      const item = await SearchHelpModal({
        urlData,
        preFilters,
        noFilter: true,
        schema: await OpervexAPI.MaterialManagement.StorageLocation.schema(),
      });

      if (!item) return;

      id = item.id;
    } else {
      if (!value) return;

      id = value;
    }

    if (!id) return;

    let storage = await OpervexAPI.MaterialManagement.StorageLocation.findId(id);

    if (!storage || storage.error) storage = {};
    else {
      if (storage.disabled) {
        alert("Depósito Inativo");
        storage = {};
      }

      if ((origin && storage.type !== operation.typeDepOrigin) || (!origin && storage.type !== operation.typeDepDestination)) {
        alert("Tipo de depósito inválido");
        storage = {};
      }
    }

    if (origin)
      setData((prev) => ({
        ...prev,
        header: { ...prev.header, originDeposit: storage.id },
        texts: { ...prev.texts, originDeposit: storage.description },
      }));
    else
      setData((prev) => ({
        ...prev,
        header: { ...prev.header, destinationDeposit: storage.id },
        texts: { ...prev.texts, destinationDeposit: storage.description },
      }));
  };

  // CRUD Handlers
  const handleButtonCreate = async () => {
    setErrors(null);

    handleSelectOperation(
      await SearchHelpModal({
        urlData: "/System/OperationGoodsMovement/Modal",
        size: "md",
        preFilters: [{ fieldname: "disabled", value: false, operation: "=" }],
        noFilter: true,
        schema: await OpervexAPI.System.OperationGoodsMovement.schema(),
      }),
    );
  };

  const handleButtonCancel = async () => {
    setPriceDifference(0);

    if (!data.header.id) {
      setPriceDifference(0);
      setDisabledField(true);
      commandAction({ ...actionsDefault, create: false }, operation.id);
    } else await searchAPI(data.header.id);
  };

  const handleButtonReversal = async () => {
    let confirm = await ConfirmService.show("Estorno", "Deseja proseguir com o estorno?");
    if (!confirm) return;

    const reason = await ReasonPromptService.show("Motivo do estorno", "Por favor, informe o motivo:");
    if (!reason) {
      alert("Operação cancelada. É necessário informar um motivo.");
      return;
    }

    const result = await OpervexAPI.MaterialManagement.GoodsMovement.reversal(data.header.id, { reason });

    if (result.errors) {
      await ConfirmService.show("Erro no Estorno", result.errors, true);
      return;
    }

    confirm = await ConfirmService.show("Estorno", "Documento estornado com sucesso, deseja abrir documento de estorno?");
    if (!confirm) await searchAPI(data.header.id);
    else await searchAPI(result.id);
  };

  const handleButtonPrint = async () => {
    setLoadingScreen(true);
    const response = await OpervexAPI.MaterialManagement.GoodsMovement.getPDF(data.header.id);
    setLoadingScreen(false);

    const pdfURL = URL.createObjectURL(response);
    window.open(pdfURL, "_blank");
  };

  const handleButtonSave = async () => {
    var body = { ...data };

    if (data.id) {
      const result = await OpervexAPI.MaterialManagement.GoodsMovement.update(body.id, body);

      if (result.errors) {
        setErrors(result.errors);
        await ConfirmService.show("Erro de Processamento", result.errors, true);
        return;
      }

      await searchAPI(data.id);
      alert("Registro atualizado com sucesso!");
    } else {
      const result = await OpervexAPI.MaterialManagement.GoodsMovement.create(body);

      if (result.errors) {
        setErrors(result.errors);
        await ConfirmService.show("Erro de Processamento", result.errors, true);
        return;
      }

      await searchAPI(result.id);
      alert("Registro criado com sucesso!");
    }

    setErrors(null);
  };

  const handleButtonOrderService = async () => {
    const url = `/${OpervexStorage.getTenantIdFromPath()}/ServiceManagement/ServiceOrder/${data.header.orderService}`;
    window.open(url, "_blank");
  };
  const handleButtonOpenGM = async (id) => {
    const url = `/${OpervexStorage.getTenantIdFromPath()}/MaterialManagement/GoodsMovement/${id}`;
    window.open(url, "_blank");
  };

  const handleButtonLog = async () => {
    await ConfirmService.show("Log", errors, true);
  };

  const handleButtonSearch = async () => {
    const startDate = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split("T")[0];

    const item = await OpervexAPI.MaterialManagement.GoodsMovement.SearchHelpModal({ documentDate: startDate });

    if (item && item.id) await searchAPI(item.id);
  };

  const handleButtonChange = async () => {
    setDisabledField(false);
    commandAction({ ...actionsDefault, save: false }, operation.id);
  };

  const searchAPI = async (id) => {
    setDisabledField(true);

    if (!id) return;

    var result = await OpervexAPI.MaterialManagement.GoodsMovement.findId(id);

    if (result && !result.error) {
      const operation = await OpervexAPI.System.OperationGoodsMovement.findId(result.header.operation);

      setOperation(operation);
      await setData(result);
      await commandAction({ ...actionsDefault, create: false, change: false, reversal: false, print: false }, operation.id);
    } else {
      setData(dataDefault);
      commandAction({ ...actionsDefault, create: false }, operation.id);
      alert(result.error);
    }

    setErrors(null);
  };

  const closeModalSerial = async () => {
    setData((prev) => ({
      ...prev,
      items: prev.items.map((row, i) =>
        i === prev.selectRow.index
          ? {
              ...row,
              serials: prev.selectRow.serials,
            }
          : row,
      ),
    }));

    setShowSerials(false);
  };

  const handleEnterKey = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const inputs = Array.from(document.querySelectorAll("#modalSerials tbody input:not([disabled]):not([readonly])"));
      const index = inputs.indexOf(e.target);
      if (index > -1 && index < inputs.length - 1) {
        inputs[index + 1].focus();
      } else if (index === inputs.length - 1) inputs[0].focus();
    }
  };

  const hiddenButtonReversal = () => {
    if (!actions.reversal) {
      if (data?.header?.orderService) return true;

      if (data?.header?.reversalType) return true;
    }

    return actions.reversal;
  };

  const hiddenButtonPrint = () => {
    if (!actions.print) {
      if (!data.header.id) return true;
    }

    return actions.print;
  };

  const visibleDep = (type) => {
    if (type === "O") {
      if (data?.header?.reversalType) {
        if (!operation?.visibleDepOrigin && data?.header?.originDeposit) return false;
        else if (operation?.visibleDepOrigin && !data?.header?.originDeposit) return true;
        else return !operation.visibleDepOrigin;
      } else {
        return !operation.visibleDepOrigin;
      }
    }

    if (type === "T") {
      if (data?.header?.reversalType) {
        if (!operation?.visibleDepDestination && data?.header?.destinationDeposit) return false;
        else if (operation?.visibleDepDestination && !data?.header?.destinationDeposit) return true;
        else return !operation.visibleDepDestination;
      } else {
        return !operation.visibleDepDestination;
      }
    }

    return true;
  };

  const handlePaste = (startIndex, e) => {
    e.preventDefault();

    const text = e.clipboardData.getData("text");

    // Divide por linha
    const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");

    setData((prev) => {
      const serials = [...prev.selectRow.serials];

      lines.forEach((line, i) => {
        const rowIndex = startIndex + i;

        if (rowIndex >= serials.length) return;

        // Aceita TAB ou ;
        const [serialNumber = "", mac = ""] = line.split(/\t|;/);

        if (prev.selectRow.controlSerial) serials[rowIndex].serialNumber = serialNumber.trim();

        if (prev.selectRow.controlMac) serials[rowIndex].mac = mac.trim();
      });

      return {
        ...prev,
        selectRow: {
          ...prev.selectRow,
          serials,
        },
      };
    });
  };

  return (
    <>
      <Container className="mt-2">
        <Form>
          {/* ----- Header -----*/}
          <Card className="mb-2">
            <Card.Header>
              <Row className="d-flex justify-content-between">
                <Col md={8}>
                  <h5>Cabeçalho</h5>
                </Col>
                <Col md={4} className="text-end">
                  <Button variant="success" size="sm" className="me-2" onClick={handleButtonCreate} hidden={actions.create}>
                    <FaPlus /> Novo
                  </Button>
                  <Button variant="primary" size="sm" className="me-2" onClick={handleButtonChange} hidden={actions.change}>
                    <FaPencilAlt /> Alterar
                  </Button>
                  <Button variant="danger" size="sm" className="me-2" onClick={handleButtonCancel} hidden={actions.save}>
                    <MdOutlineCancel /> Cancelar
                  </Button>
                  <Button variant="primary" size="sm" className="me-2" onClick={handleButtonSave} hidden={actions.save}>
                    <FaSave /> Salvar
                  </Button>
                  <Button variant="secondary" size="sm" className="me-2" onClick={handleButtonPrint} hidden={hiddenButtonPrint()}>
                    <FaPrint /> Imprimir
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    className="me-2"
                    onClick={handleButtonReversal}
                    hidden={hiddenButtonReversal()}
                  >
                    <FaSave /> Estornar
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Código</Form.Label>
                    <InputGroup>
                      <Form.Control type="text" size="sm" value={data.header.id} name="header.id" disabled={true} />

                      <InputGroup.Text size="sm" type="button" onClick={handleButtonSearch}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Descrição</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      name="header.description"
                      maxLength={50}
                      value={data.header.description}
                      onChange={handleChange}
                      disabled={disabledField}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Operação</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      name="text"
                      maxLength={50}
                      value={operation.id ? operation.id + " - " + operation.text : ""}
                      onChange={handleChange}
                      disabled={true}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-2">
            <Card.Header>
              <Row className="d-flex justify-content-between">
                <Col md={8}>
                  <h5>Dados Básicos</h5>
                </Col>
                <Col md={4} className="text-end">
                  <Button variant="warning" size="sm" className="me-2" onClick={handleButtonLog} hidden={!errors}>
                    <RiErrorWarningLine /> Log
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Data Documento</Form.Label>
                    <Form.Control
                      type="date"
                      name="header.documentDate"
                      size="sm"
                      value={data.header.documentDate || ""}
                      onChange={handleChange}
                      disabled={disabledField}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <CustomCurrency
                    label={"Montante"}
                    value={data.header.amount}
                    onChange={(e) => handleChange({ target: { name: "header.amount", value: e } })}
                    disabled={disabledField}
                    showDisabled={true}
                    hidden={!operation.visibleAmount}
                  />
                </Col>
                <Col md={2}>
                  <CustomCurrency
                    label={"Diferença"}
                    value={priceDifference}
                    onChange={(e) => handleChange({ target: { name: "priceDifference", value: e } })}
                    disabled={true}
                    showDisabled={true}
                    hidden={!operation.visibleAmount || disabledField}
                  />
                </Col>
                <Col md={2}>
                  <Form.Group hidden={visibleDep("O")}>
                    <Form.Label>Depósito de Origem</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={data.header.originDeposit ? data.header.originDeposit : ""}
                        name="header.originDeposit"
                        onChange={handleChange}
                        disabled={disabledField}
                        onFocus={(event) => handleFocus(event, null, "originDeposit", null)}
                        onBlur={(event) => handleBlur(event, null, "originDeposit", null)}
                      />

                      <InputGroup.Text size="sm" type="button" onClick={(e) => handleStorageLocation(true, true)}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group hidden={visibleDep("O")}>
                    <Form.Label>Descrição</Form.Label>
                    <InputGroup>
                      <Form.Control type="text" size="sm" value={data?.texts?.originDeposit} disabled={true} />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Referência</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      name="header.reference"
                      maxLength={20}
                      value={data.header.reference}
                      onChange={handleChange}
                      disabled={disabledField}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group hidden={!operation.visibleAcessKey}>
                    <Form.Label>Chave de Acesso</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      name="header.accesskey"
                      maxLength={44}
                      value={data.header.accesskey}
                      onChange={handleChange}
                      disabled={disabledField}
                    />
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group hidden={visibleDep("T")}>
                    <Form.Label>Depósito de Destino</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={data.header.destinationDeposit ? data.header.destinationDeposit : ""}
                        name="header.destinationDeposit"
                        onChange={handleChange}
                        disabled={disabledField}
                        onFocus={(event) => handleFocus(event, null, "destinationDeposit", null)}
                        onBlur={(event) => handleBlur(event, null, "destinationDeposit", null)}
                      />

                      <InputGroup.Text size="sm" type="button" onClick={(e) => handleStorageLocation(false, true)}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group hidden={visibleDep("T")}>
                    <Form.Label>Descrição</Form.Label>
                    <InputGroup>
                      <Form.Control type="text" size="sm" value={data?.texts?.destinationDeposit} disabled={true} />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={2} hidden={!data.header.orderService}>
                  <Form.Group>
                    <Form.Label>Ordem de Serviço</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={data.header.orderService ? data.header.orderService : ""}
                        name="header.orderService"
                        disabled={true}
                      />

                      <InputGroup.Text size="sm" type="button" onClick={handleButtonOrderService}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={2} hidden={!(data.header.reversalType === 1)}>
                  <Form.Group>
                    <Form.Label>Documento de Estorno</Form.Label>
                    <InputGroup>
                      <Form.Control type="text" size="sm" value={data.header.reversal ?? ""} name="header.reversal" disabled />

                      <InputGroup.Text role="button" onClick={() => handleButtonOpenGM(data.header.reversal)}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={2} hidden={!(data.header.reversalType === 2)}>
                  <Form.Group>
                    <Form.Label>Documento Estornado</Form.Label>
                    <InputGroup>
                      <Form.Control type="text" size="sm" value={data.header.reversal ?? ""} name="header.reversal" disabled />

                      <InputGroup.Text role="button" onClick={() => handleButtonOpenGM(data.header.reversal)}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-2">
            <Card.Header>
              <Row className="d-flex justify-content-between">
                <Col md={8}>
                  <h5>Produtos</h5>
                </Col>
                <Col md={4} className="text-end">
                  <Button variant="primary" size="sm" onClick={handleAddRow} hidden={actions.save}>
                    <FaPlus />
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr className="text-center align-middle">
                      <th width="5%">Item</th>
                      <th width="15%">Código</th>
                      <th width="40%">Descrição</th>
                      <th width="10%">Quantidade</th>
                      <th width="5%">Unidade</th>
                      {operation.visibleAmount ? <th width="15%">Preço Unitário</th> : ""}
                      <th width={disabledField ? "5%" : "10%"}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.length > 0 ? (
                      data.items.map((row, index) => (
                        <tr key={index}>
                          <td className="text-center">{row.item}</td>
                          <td className="text-center">
                            {disabledField ? (
                              row.product
                            ) : (
                              <InputGroup>
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  name="product"
                                  value={row.product}
                                  onChange={(e) => handleChangeRow(index, e)}
                                  disabled={disabledField}
                                  onFocus={(event) => handleFocus(event, index, "product", row)}
                                  onBlur={(event) => handleBlur(event, index, "product", row)}
                                />
                                <InputGroup.Text
                                  size="sm"
                                  type="button"
                                  onClick={(e) => handleSelectProduct({ index: index, id: null, onClick: true })}
                                >
                                  <FaSearch />
                                </InputGroup.Text>
                              </InputGroup>
                            )}
                          </td>
                          <td>{row.description}</td>
                          <td className="text-end">
                            <QuantityCustom
                              value={row.quantity}
                              onChange={(e) => handleChangeRow(index, { target: { name: "quantity", value: e } })}
                              disabled={disabledField}
                              showDisabled={false}
                              decimal={row.controlSerial ? 0 : 3}
                            />
                          </td>
                          <td className="text-center">
                            {disabledField ? (
                              row.unitMeasurement
                            ) : (
                              <Form.Control
                                type="text"
                                size="sm"
                                name="unitMeasurement"
                                value={row.unitMeasurement ? row.unitMeasurement : ""}
                                onChange={(e) => handleChangeRow(index, e)}
                                disabled={disabledField}
                              />
                            )}
                          </td>
                          {operation.visibleAmount ? (
                            <td className="text-end">
                              <CustomCurrency
                                value={row.priceUnit}
                                onChange={(e) => handleChangeRow(index, { target: { name: "priceUnit", value: e } })}
                                disabled={disabledField}
                                decimal={6}
                                showDisabled={false}
                              />
                            </td>
                          ) : (
                            ""
                          )}

                          <td className="text-center">
                            {row.controlSerial ? (
                              <Button
                                variant="primary"
                                className={disabledField ? "" : "me-3"}
                                onClick={() => handleSerials(index, row)}
                                size="sm"
                              >
                                <FaRegListAlt />
                              </Button>
                            ) : (
                              <span className="me-3 d-inline-block" style={{ width: "32px" }}></span>
                            )}
                            {!disabledField ? (
                              <Button variant="danger" onClick={() => handleRemoveRow(index)} size="sm">
                                <FaTrash />
                              </Button>
                            ) : (
                              ""
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center">
                          Nenhuma produto encontrado
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Row>
            </Card.Body>
          </Card>
        </Form>
      </Container>

      <Modal show={showSerials} onHide={() => closeModalSerial()} size={"md"} id="modalSerials">
        <Modal.Header closeButton>
          <Modal.Title>
            <h6>
              {data?.selectRow?.item} - {data?.selectRow?.description}
            </h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div hidden={!operation.visibleDepOrigin}>
            <Button
              variant="primary"
              className="w-100 btn-sm"
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
              onClick={handleSelectSerial}
              disabled={!data.header.originDeposit}
              hidden={disabledField}
            >
              <FaRegListAlt style={{ marginRight: "8px" }} /> Selecionar itens
            </Button>
            <br />
          </div>
          <Table striped bordered hover size="sm">
            <thead>
              <tr className="text-center align-middle">
                <th width="10%">Sequencia</th>
                {data?.selectRow?.controlSerial ? <th width={data?.selectRow?.controlMac ? "45%" : " 90%"}>Nº Série</th> : ""}
                {data?.selectRow?.controlMac ? <th width={data?.selectRow?.controlSerial ? "45%" : " 90%"}>MAC</th> : ""}
              </tr>
            </thead>
            <tbody>
              {data?.selectRow?.serials?.length > 0 ? (
                data.selectRow.serials.map((row, index) => (
                  <tr key={index}>
                    <td className="text-center">{row.sequential}</td>
                    {data.selectRow.controlSerial && (
                      <td className="text-center">
                        {disabledField ? (
                          row.serialNumber
                        ) : (
                          <Form.Control
                            type="text"
                            size="sm"
                            name="serialNumber"
                            id={"serialNumber" + index}
                            value={row.serialNumber}
                            onChange={(e) => handleChangeRowSerial(index, e)}
                            onPaste={(e) => handlePaste(index, e)}
                            className="rounded-0"
                            onKeyDown={handleEnterKey}
                          />
                        )}
                      </td>
                    )}

                    {data.selectRow.controlMac && (
                      <td className="text-center" onKeyDownCapture={handleEnterKey}>
                        {disabledField ? (
                          row.mac
                        ) : (
                          <MaskedFormControl
                            type="text"
                            size="sm"
                            mask="**:**:**:**:**:**"
                            name="mac"
                            id={"mac" + index}
                            value={row.mac}
                            onChange={(e) => handleChangeRowSerial(index, e)}
                            onPaste={(e) => handlePaste(index, e)}
                            className="rounded-0"
                            onKeyDown={handleEnterKey}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={data?.selectRow?.controlMac && data?.selectRow?.controlSerial ? 3 : 2} className="text-center">
                    Nenhumo registro encontrado
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      <Modal show={showReason} onHide={() => setShowReason(false)} size={"md"} id="modalSerials">
        <Modal.Header closeButton>
          <Modal.Title>
            <h6>Motivo do Estorno</h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            size="sm"
            name="header.reason"
            maxLength={150}
            value={data.header.reason}
            onChange={handleChange}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default OperationGoodsMovement;
